import React, { useEffect, useState, useRef, createElement } from 'react';
interface ScoreBarChartProps {
  scores: Record<string, number>;
  mainBrand: string;
  findCompetitorUrl: (brand: string) => string;
  getFavicon: (url: string) => string;
}
const ScoreBarChart: React.FC<ScoreBarChartProps> = ({
  scores,
  mainBrand,
  findCompetitorUrl,
  getFavicon
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<any | null>(null);
  const [chartJSLoaded, setChartJSLoaded] = useState(false);
  // Load Chart.js from CDN
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if Chart.js is already loaded
      if (window.Chart) {
        setChartJSLoaded(true);
        return;
      }
      // Load Chart.js from CDN
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
      script.async = true;
      script.onload = () => {
        setChartJSLoaded(true);
      };
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, []);
  // Create and update chart
  useEffect(() => {
    if (!chartRef.current || !chartJSLoaded || !window.Chart) return;
    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    // Sort brands by score (descending)
    const sortedBrands = Object.entries(scores).sort(([, a], [, b]) => b - a).map(([brand]) => brand);
    // Prepare data for bar chart
    const labels = sortedBrands;
    const data = sortedBrands.map(brand => scores[brand] || 0);
    // Use the same blue color for all brands
    const backgroundColor = Array(sortedBrands.length).fill('rgba(22, 73, 255, 0.8)');
    const borderColor = Array(sortedBrands.length).fill('rgb(22, 73, 255)');
    // Create chart
    const ctx = chartRef.current.getContext('2d');
    if (ctx) {
      chartInstance.current = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Score',
            data,
            backgroundColor,
            borderColor,
            borderWidth: 1
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              beginAtZero: true,
              max: 100,
              grid: {
                display: true
              }
            },
            y: {
              grid: {
                display: false
              },
              ticks: {
                font: {
                  size: 16 // Increased font size for brand names
                }
              }
            }
          },
          plugins: {
            legend: {
              display: false // Hide legend
            },
            title: {
              display: false // Ensure title is not displayed
            },
            tooltip: {
              callbacks: {
                title: function (tooltipItems) {
                  return tooltipItems[0].label;
                },
                label: function (context) {
                  return `Score: ${context.raw}`;
                }
              },
              displayColors: false // Hide color box in tooltip
            },
            chartJsPluginScoreLabels: {
              id: 'chartJsPluginScoreLabels',
              afterDatasetsDraw(chart) {
                const {
                  ctx,
                  data,
                  scales
                } = chart;
                const xScale = scales.x;
                const yScale = scales.y;
                ctx.font = '12px Arial';
                ctx.fillStyle = '#000';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                data.datasets[0].data.forEach((value, index) => {
                  const x = xScale.getPixelForValue(value) + 5;
                  const y = yScale.getPixelForValue(index);
                  ctx.fillText(value.toString(), x, y);
                });
              }
            }
          }
        }
      });
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [scores, mainBrand, chartJSLoaded]);
  // Fallback to simple HTML bars if Chart.js fails to load
  const renderFallbackBars = () => {
    // Sort brands by score (descending)
    const sortedBrands = Object.entries(scores).sort(([, a], [, b]) => b - a);
    return <div className="space-y-4 w-full" data-id="element-801" data-mp="2slwzprj8592m">
        {sortedBrands.map(([brand, score]) => <div key={brand} className="flex flex-col" data-id="element-802" data-mp="2ntdkkq4w25vp">
            <div className="flex items-center mb-1" data-id="element-803" data-mp="2ivv9ou7b9gl2">
              <img src={getFavicon(findCompetitorUrl(brand))} alt={`${brand} logo`} className="w-5 h-5 mr-2" onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
          }} data-id="element-804" data-mp="2ivbsjfphiw83" />
              <span className="text-base font-medium text-gray-900" data-id="element-805" data-mp="3nv0ifscuce76">
                {brand}
              </span>
              <span className="ml-auto text-sm font-bold" data-id="element-806" data-mp="3svockmwd9b5m">{score}</span>
            </div>
            <div className="flex items-center w-full" data-id="element-807" data-mp="1olldvgj34ve">
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-808" data-mp="5pnl0decxixw">
                <div className="h-2.5 rounded-full bg-[#1649ff]" style={{
              width: `${score}%`
            }} data-id="element-809" data-mp="anyvmqch7xh6"></div>
              </div>
              <span className="ml-2 text-sm font-bold" data-id="element-810" data-mp="kdiehw06vrc1">{score}</span>
            </div>
          </div>)}
      </div>;
  };
  return <div className="w-full" data-id="element-811" data-mp="pe66ol05me7u">
      <div className="w-full h-[400px]" data-id="element-812" data-mp="pb1rytp2c02x">
        {!chartJSLoaded ? renderFallbackBars() : <canvas ref={chartRef} data-id="element-813" data-mp="2qf41yn13hcez"></canvas>}
      </div>
    </div>;
};
export default ScoreBarChart;